import { useEffect, useRef, useState } from "react";
import { Server, ShieldCheck, Activity, Cpu } from "lucide-react";

const LOG_LINES = [
  { prompt: "qv>", text: "provision cyber-range --scenario web-pivot" },
  { prompt: "", text: "✓ attack box ready  ·  ecs.g6.xlarge" },
  { prompt: "", text: "✓ VPN certificate issued  ·  tunnel up" },
  { prompt: "", text: "✓ topology validated  ·  6 nodes linked" },
];

const STATUS_ROWS = [
  { icon: Server, label: "Attack Box", value: "Running", tone: "ok" },
  { icon: ShieldCheck, label: "VPN Tunnel", value: "Secure", tone: "ok" },
  { icon: Activity, label: "Flow Run", value: "Live", tone: "live" },
];

const CLUSTER_NODES = [
  { top: "8px", left: "12px" },
  { top: "26px", left: "60px" },
  { top: "6px", left: "108px" },
  { top: "44px", left: "92px" },
  { top: "42px", left: "18px" },
];

/* Reflects the OS "reduce motion" setting; initial value read synchronously
 * so we never flash an animation the user asked us not to play. */
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduce;
}

/* Types each log line in order, then reveals `rowCount` status rows on a
 * stagger. When looping, only the terminal text re-types; the rows stay put. */
function useProvisioningSequence(
  lines,
  rowCount,
  { speed = 22, loop = true } = {},
) {
  const reduce = usePrefersReducedMotion();
  const [typed, setTyped] = useState(() => lines.map(() => ""));
  const [rows, setRows] = useState(0);

  useEffect(() => {
    if (reduce) {
      setTyped(lines.map((l) => l.text));
      setRows(rowCount);
      return;
    }

    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    const typeAll = async () => {
      for (let i = 0; i < lines.length && !cancelled; i++) {
        const full = lines[i].text;
        setTyped((prev) => {
          const next = [...prev];
          next[i] = "";
          return next;
        });
        for (let c = 1; c <= full.length && !cancelled; c++) {
          setTyped((prev) => {
            const next = [...prev];
            next[i] = full.slice(0, c);
            return next;
          });
          await wait(speed);
        }
      }
    };

    (async () => {
      await typeAll();
      for (let r = 1; r <= rowCount && !cancelled; r++) {
        setRows(r);
        await wait(220);
      }
      while (loop && !cancelled) {
        await wait(5000);
        setTyped(lines.map(() => ""));
        await wait(300);
        await typeAll();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce]);

  return { typed, rows, reduce };
}

function TopologyCluster({ reduce }) {
  return (
    <div className="lp-float-delayed absolute -bottom-8 -left-6 z-20 hidden sm:block lg:-bottom-10 lg:-left-12">
      <div
        className="qv-card qv-noise w-40 p-3"
        style={{
          background:
            "linear-gradient(var(--color-bg-card), var(--color-bg-card)), #141d19",
          boxShadow: "0 18px 40px -18px rgba(0,0,0,0.6)",
        }}
      >
        <div className="mb-2 flex items-center gap-2">
          <Cpu className="h-3.5 w-3.5 text-(--color-primary)" />
          <span className="qv-mono text-[9.5px] uppercase tracking-wider text-(--color-text-muted)">
            Topology
          </span>
        </div>
        <div className="relative h-16">
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 128 64"
            fill="none"
          >
            <line
              x1="16"
              y1="14"
              x2="64"
              y2="30"
              stroke="var(--color-primary)"
              strokeOpacity="0.35"
              strokeWidth="1"
            />
            <line
              x1="64"
              y1="30"
              x2="112"
              y2="12"
              stroke="var(--color-primary)"
              strokeOpacity="0.35"
              strokeWidth="1"
            />
            <line
              x1="64"
              y1="30"
              x2="96"
              y2="48"
              stroke="var(--color-primary)"
              strokeOpacity="0.35"
              strokeWidth="1"
            />
            <line
              x1="16"
              y1="14"
              x2="22"
              y2="46"
              stroke="var(--color-primary)"
              strokeOpacity="0.35"
              strokeWidth="1"
            />

            {/* data packets travelling the links */}
            {!reduce && (
              <>
                <circle r="1.6" fill="var(--color-primary)">
                  <animateMotion
                    dur="2.6s"
                    repeatCount="indefinite"
                    path="M16,14 L64,30"
                  />
                </circle>
                <circle r="1.6" fill="var(--color-primary)">
                  <animateMotion
                    dur="3.2s"
                    repeatCount="indefinite"
                    path="M64,30 L112,12"
                  />
                </circle>
                <circle r="1.6" fill="var(--color-primary)">
                  <animateMotion
                    dur="2.9s"
                    repeatCount="indefinite"
                    path="M64,30 L96,48"
                  />
                </circle>
              </>
            )}
          </svg>
          {CLUSTER_NODES.map((pos, i) => (
            <span
              key={i}
              className="absolute h-2.5 w-2.5 rounded-full bg-(--color-primary) shadow-[0_0_8px_var(--color-primary)]"
              style={{
                ...pos,
                animation: reduce
                  ? "none"
                  : `lp-float ${6 + i}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HeroVisual() {
  const { typed, rows, reduce } = useProvisioningSequence(
    LOG_LINES,
    STATUS_ROWS.length,
  );
  const lastTypingLine = useRef(0);

  // index of the deepest line that has content, for cursor placement
  const activeLine = typed.reduce((acc, t, i) => (t ? i : acc), 0);
  lastTypingLine.current = activeLine;

  return (
    <div className="relative mx-auto w-full max-w-md">
      {/* Console + its own ambient glow — anchors the floating accent cards */}
      <div className="relative">
        <div
          className="pointer-events-none absolute -inset-10 rounded-4xl opacity-60 blur-3xl"
          style={{
            background:
              "radial-gradient(50% 50% at 62% 24%, color-mix(in srgb, var(--color-primary) 30%, transparent), transparent 72%)",
          }}
        />

        <div
          className="lp-float relative overflow-hidden rounded-2xl qv-card qv-noise"
          style={{
            boxShadow:
              "0 40px 90px -30px rgba(0,0,0,0.85), 0 0 60px -28px color-mix(in srgb, var(--color-primary) 60%, transparent)",
          }}
        >
          {/* light sweeping across the top edge */}
          <span className="lp-edge-sweep" aria-hidden="true" />

          {/* title bar */}
          <div className="flex items-center gap-2 border-b border-(--color-border) px-4 py-3">
            <span className="h-3 w-3 rounded-full bg-[#f87171]/80" />
            <span className="h-3 w-3 rounded-full bg-[#fbbf24]/80" />
            <span className="h-3 w-3 rounded-full bg-(--color-primary)" />
            <span className="qv-mono ml-2 text-[11px] text-(--color-text-muted)">
              range-orchestrator
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <span className="cyber-badge-dot" />
              <span className="qv-mono text-[10px] uppercase tracking-wider text-(--color-primary)">
                online
              </span>
            </span>
          </div>

          {/* terminal body with scan line */}
          <div className="relative px-4 py-4 qv-mono text-[12px] leading-relaxed">
            <span className="lp-scan-line pointer-events-none absolute left-0 right-0 h-16 bg-[linear-gradient(180deg,transparent,var(--color-primary-50),transparent)]" />
            {LOG_LINES.map((line, i) => (
              <div key={i} className="flex min-h-[1.4em] gap-2">
                {line.prompt && typed[i] && (
                  <span className="font-semibold text-(--color-primary)">
                    {line.prompt}
                  </span>
                )}
                <span
                  style={{
                    color:
                      i === 0
                        ? "color-mix(in srgb, var(--color-primary) 55%, #d9fff0)"
                        : "var(--color-primary)",
                  }}
                >
                  {typed[i]}
                </span>
              </div>
            ))}
            <span className="inline-block h-4 w-2 animate-pulse bg-(--color-primary) align-middle" />
          </div>

          {/* status rows — revealed on a stagger once typing completes */}
          <div className="space-y-2.5 px-4 pb-4 pt-1">
            {STATUS_ROWS.map((row, i) => {
              const Icon = row.icon;
              const shown = i < rows;
              return (
                <div
                  key={row.label}
                  className="flex items-center gap-3 rounded-xl border border-(--color-border) px-4 py-3"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    opacity: shown ? 1 : 0,
                    transform: shown ? "translateY(0)" : "translateY(8px)",
                    transition: "opacity .5s ease, transform .5s ease",
                  }}
                >
                  <span
                    className="grid h-8 w-8 place-items-center rounded-lg"
                    style={{
                      background:
                        "color-mix(in srgb, var(--color-primary) 12%, transparent)",
                    }}
                  >
                    <Icon className="h-4 w-4 text-(--color-primary)" />
                  </span>
                  <span className="text-sm font-semibold text-(--color-text-primary)">
                    {row.label}
                  </span>
                  <span className="ml-auto flex items-center gap-1.5 text-[13px] font-medium text-(--color-primary)">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-(--color-primary) shadow-[0_0_8px_var(--color-primary)]" />
                    {row.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Uptime chip — tucked onto the console's top-right corner */}
        <div className="lp-float-slow absolute -top-7 -right-3 z-20 hidden sm:block lg:-top-8 lg:-right-6">
          <div
            className="qv-card relative rounded-xl px-4 py-3"
            style={{
              background:
                "linear-gradient(var(--color-bg-card), var(--color-bg-card)), #141d19",
              boxShadow: "0 18px 40px -18px rgba(0,0,0,0.6)",
            }}
          >
            <span className="lp-uptime-ring" aria-hidden="true" />
            <div className="qv-num text-2xl text-(--color-text-primary)">
              99.9%
            </div>
            <div className="qv-mono text-[9.5px] uppercase tracking-wider text-(--color-text-muted)">
              Range Uptime
            </div>
          </div>
        </div>

        {/* Topology — tucked onto the console's bottom-left corner */}
        <TopologyCluster reduce={reduce} />
      </div>
    </div>
  );
}
